import dayjs from 'dayjs'
import timezone from 'dayjs/plugin/timezone'
import utc from 'dayjs/plugin/utc'
import { object, number, date, InferType, string, boolean } from 'yup'

import {
  ComponentLimitType,
  ComponentUsageLog,
  MaintenanceCadenceType,
  MaintenanceItem,
  MaintenanceNextDue,
  PartsTransaction,
} from '../graphql'

import { CadenceValue, cadenceValueSchema } from './cadenceValue'
import { UNIT_MAP } from './remainingValue'
import { formatDateForDisplayInUtc } from '../helpers'
import { areMaintenanceNextDuesEffectivelyEqual } from '../maintenanceItem'

dayjs.extend(timezone)
dayjs.extend(utc)

const unitValueSchema = number()
  .positive()
  .optional()
  .default(undefined)
  .transform((value, originalValue) => {
    if (originalValue === undefined) return undefined
    if (originalValue === null) return undefined
    if (originalValue === '') return undefined
    if (typeof originalValue === 'number') {
      return originalValue
    }
    if (typeof originalValue === 'string') {
      return parseFloat(value)
    }
    return value
  })

export const nextDueValueSchema = object({
  date: date()
    .nullable()
    .default(undefined)
    .transform((value, originalValue) => {
      if (originalValue === undefined) return undefined
      if (originalValue === null) return undefined
      if (originalValue === '') return undefined
      return value
    }),
  flying_hours: unitValueSchema,
  cycles: unitValueSchema,
  landings: unitValueSchema,
  allow_tolerance: boolean().optional().default(false),
  note: string().optional().default(undefined),
  category: string().optional().default(undefined),
})

interface PreviousComplianceInformation {
  previousNextDueValue: NextDueValue
}

interface PartInstallationAdjustment {
  transactionInfo: Partial<PartsTransaction>
  ComponentLimitType: ComponentLimitType
}

export const getNextDueAsFlatDict = (nextDueValue: NextDueValue) => {
  const result = {}
  const fields = ['date', 'flying_hours', 'cycles', 'landings']
  const fieldMap = {
    date: 'Date',
    flying_hours: 'Hours',
    cycles: 'Cycles',
    landings: 'Landings',
  }
  fields.forEach((field) => {
    if (nextDueValue?.[field]) {
      result['nextDue' + fieldMap[field]] = nextDueValue[field]
    } else {
      result['nextDue' + fieldMap[field]] = null
    }
  })
  return result
}

export const getNextDueValueAsStringArray = (nextDueValue: NextDueValue) => {
  const result: string[] = []
  const fields = ['date', 'flying_hours', 'cycles', 'landings']
  fields.forEach((field) => {
    if (nextDueValue[field]) {
      if (field === 'date') {
        result.push(formatDateForDisplayInUtc(nextDueValue[field]))
      } else {
        result.push(`${nextDueValue[field]} ${UNIT_MAP[field]}`)
      }
    }
  })
  return result
}

export const calculateNextDueValue = (
  cadenceType: MaintenanceCadenceType,
  cadenceValue: CadenceValue,
  componentUsageLogOfLastCompliance: Partial<ComponentUsageLog>,
  dateOfLastCompliance: Date,
  isEndOfMonthAdjustment: boolean,
  previousComplianceInformation?: PreviousComplianceInformation | null,
  partsInformation?: PartInstallationAdjustment | null
): NextDueValue => {
  cadenceValue = cadenceValueSchema.cast(cadenceValue)

  const calculateNextDueValue = (
    previousValue: Partial<ComponentUsageLog>,
    cadenceValue: CadenceValue,
    cadenceType: MaintenanceCadenceType,
    lastComplianceDate: Date,
    isEndOfMonthAdjustment: boolean
  ): NextDueValue => {
    const nextDueValue: NextDueValue = {}
    const monthCalculation = () => {
      if (cadenceValue['months'] === undefined || !lastComplianceDate)
        return undefined
      if (isEndOfMonthAdjustment) {
        return dayjs
          .tz(lastComplianceDate)
          .utc()
          .add(cadenceValue['months'], 'months')
          .endOf('month')
          .endOf('day') // This is an issue for countries that is west of UTC but works for the US
          .toDate()
      } else {
        return dayjs
          .tz(lastComplianceDate)
          .utc()
          .add(cadenceValue['months'], 'months')
          .toDate()
      }
    }
    Object.keys(cadenceValue).forEach((key) => {
      if (
        key === 'days' &&
        cadenceValue['days'] !== undefined &&
        lastComplianceDate
      ) {
        nextDueValue['date'] = dayjs
          .tz(lastComplianceDate)
          .add(cadenceValue['days'], 'days')
          .toDate()
      }
      if (key === 'months' && cadenceValue['months'] !== undefined) {
        nextDueValue['date'] = monthCalculation()
      }
      if (key === 'cycles' && cadenceValue['cycles'] !== undefined) {
        nextDueValue['cycles'] =
          cadenceValue['cycles'] + previousValue.cycleSinceNew
      }
      if (
        key === 'flying_hours' &&
        cadenceValue['flying_hours'] !== undefined
      ) {
        nextDueValue['flying_hours'] =
          cadenceValue['flying_hours'] + previousValue.totalTimeSinceNew
      }
      if (key === 'landings' && cadenceValue['landings'] !== undefined) {
        nextDueValue['landings'] =
          cadenceValue['landings'] + previousValue.cycleSinceNew
      }
    })
    return nextDueValue
  }
  const getAdjustedCadenceValue = (): CadenceValue => {
    const adjustedCadenceValue: CadenceValue = {}
    const handleDefaultValue = (value: number | undefined) => {
      if (value === undefined) return 0
      if (value === null) return 0
      if (value < 0) return 0
      return value
    }
    const LIMIT_TYPE_TO_FIELD_NAME = {
      LIFE_LIMITED: {
        flying_hours: 'hoursSinceNew',
        cycles: 'cyclesSinceNew',
        landings: 'cyclesSinceNew',
        months: 'monthsSinceNew',
      },
      OVERHAUL: {
        flying_hours: 'hoursSinceOverhaul',
        cycles: 'cyclesSinceOverhaul',
        landings: 'cyclesSinceOverhaul',
        months: 'monthsSinceOverhaul',
      },
    }
    const mappedFields =
      LIMIT_TYPE_TO_FIELD_NAME[partsInformation.ComponentLimitType]
    if (mappedFields) {
      Object.keys(mappedFields).forEach((key) => {
        if (cadenceValue[key] !== undefined) {
          adjustedCadenceValue[key] =
            cadenceValue[key] -
            handleDefaultValue(
              partsInformation.transactionInfo[mappedFields[key]]
            )
        }
      })
      return adjustedCadenceValue
    }
    return cadenceValue
  }
  const getAdjustedPreviousCompliance = () => {
    //TODO: Identify if the previous Compliance took place in the tolerance window
    // If so, adjust the current compliance timestamp by the tolerance window
    const adjustedComponentUsageLog: Partial<ComponentUsageLog> = {
      ...componentUsageLogOfLastCompliance,
    }
    let adjustedComplianceDate = dateOfLastCompliance

    const mappedFields = {
      flying_hours: 'totalTimeSinceNew',
      cycles: 'cycleSinceNew',
      landings: 'cycleSinceNew',
    }

    Object.keys(mappedFields).forEach((key) => {
      if (cadenceValue[key] !== undefined) {
        const marginOfError = Math.abs(
          componentUsageLogOfLastCompliance[mappedFields[key]] -
            previousComplianceInformation.previousNextDueValue[key]
        )
        if (
          cadenceValue.tolerance?.[key] &&
          marginOfError <= cadenceValue.tolerance[key]
        ) {
          adjustedComponentUsageLog[mappedFields[key]] =
            previousComplianceInformation.previousNextDueValue[key]
        }
      }
    })

    if (cadenceValue['months'] !== undefined) {
      const marginOfError = Math.abs(
        dayjs(dateOfLastCompliance).diff(
          previousComplianceInformation.previousNextDueValue.date,
          'month'
        )
      )
      if (
        cadenceValue.tolerance?.months &&
        marginOfError < cadenceValue.tolerance?.months
      ) {
        adjustedComplianceDate =
          previousComplianceInformation.previousNextDueValue.date
      }
    }

    return [adjustedComponentUsageLog, adjustedComplianceDate]
  }

  // Case 1: No Previous Compliance Information or Parts Information
  if (!previousComplianceInformation && !partsInformation) {
    return {
      ...calculateNextDueValue(
        componentUsageLogOfLastCompliance,
        cadenceValue,
        cadenceType,
        dateOfLastCompliance,
        isEndOfMonthAdjustment
      ),
      note: 'Next due calculated based on the current compliance information',
    }
  }

  // Case 2: No Previous Compliance Information but Parts Information
  if (!previousComplianceInformation && partsInformation) {
    // Adjust the cadence value based on the part installation
    const adjustedCadenceValue = getAdjustedCadenceValue()
    return {
      ...calculateNextDueValue(
        componentUsageLogOfLastCompliance,
        adjustedCadenceValue,
        cadenceType,
        dateOfLastCompliance,
        isEndOfMonthAdjustment
      ),
      //TODO: only if adjustment took place
      note: 'Next due adjusted based on part installation',
    }
  }

  // Case 3: Previous Compliance Information but no Parts Information
  if (previousComplianceInformation && !partsInformation) {
    // Adjust the current timestamp if the compliance took place in tolerance window
    const [adjustedPreviousCompliance, adjustComplianceDate] =
      getAdjustedPreviousCompliance()
    return {
      ...calculateNextDueValue(
        adjustedPreviousCompliance as ComponentUsageLog,
        cadenceValue,
        cadenceType,
        adjustComplianceDate as Date,
        isEndOfMonthAdjustment
      ),
      //TODO: only if adjustment took place
      note: 'Next due calculated based on previous compliance information',
    }
  }

  // Case 4: Previous Compliance Information and Parts Information
  if (previousComplianceInformation && partsInformation) {
    // Adjust the current timestamp if the compliance took place in tolerance window
    // Adjust the cadence value based on the part installation
    const adjustedCadenceValue = getAdjustedCadenceValue()
    const [adjustedPreviousCompliance, adjustComplianceDate] =
      getAdjustedPreviousCompliance()
    return {
      ...calculateNextDueValue(
        adjustedPreviousCompliance as ComponentUsageLog,
        adjustedCadenceValue,
        cadenceType,
        adjustComplianceDate as Date,
        isEndOfMonthAdjustment
      ),
      //TODO: only if adjustment took place
      note: 'Next due calculated based on previous compliance and part installation',
    }
  }
}

export type NextDueValue = InferType<typeof nextDueValueSchema>

/**
 * Compares two NextDueValue objects for effective equality, considering overrides.
 * This function checks if the effective values are equal, not necessarily the
 * absolute values.
 *
 * @param value1 - First NextDueValue to compare
 * @param value2 - Second NextDueValue to compare
 * @returns true if the effective values are equal, false otherwise
 */
export const areNextDueValuesEffectivelyEqual = (
  value1: NextDueValue,
  value2: NextDueValue
): boolean => {
  // Fields to compare
  const fields = ['date', 'flying_hours', 'cycles', 'landings']

  // Check each field for equality
  for (const field of fields) {
    // Skip undefined or null values in both objects
    if (
      (value1[field] === undefined || value1[field] === null) &&
      (value2[field] === undefined || value2[field] === null)
    ) {
      continue
    }

    // If one has the field but the other doesn't, they're not equal
    if (
      value1[field] === undefined ||
      value1[field] === null ||
      value2[field] === undefined ||
      value2[field] === null
    ) {
      return false
    }

    // For date fields, compare using dayjs
    if (field === 'date') {
      const day1 = dayjs(value1.date).utc()
      const day2 = dayjs(value2.date).utc()
      if (!day1.isSame(day2, 'day')) {
        return false
      }
    } else {
      // For numeric fields, compare directly
      if (value1[field] !== value2[field]) {
        return false
      }
    }
  }

  return true
}

/**
 * Compares two NextDueValue objects for absolute equality.
 * This function checks if the raw values are exactly equal without
 * considering context or normalization.
 *
 * @param value1 - First NextDueValue to compare
 * @param value2 - Second NextDueValue to compare
 * @returns true if the absolute values are equal, false otherwise
 */
export const areNextDueValuesAbsolutelyEqual = (
  value1: NextDueValue,
  value2: NextDueValue
): boolean => {
  // Fields to compare
  const fields = [
    'date',
    'flying_hours',
    'cycles',
    'landings',
    'allow_tolerance',
    'note',
    'category',
  ]

  // Check each field for equality
  for (const field of fields) {
    // Skip undefined or null values in both objects
    if (
      (value1[field] === undefined || value1[field] === null) &&
      (value2[field] === undefined || value2[field] === null)
    ) {
      continue
    }

    // If one has the field but the other doesn't, they're not equal
    if (
      value1[field] === undefined ||
      value1[field] === null ||
      value2[field] === undefined ||
      value2[field] === null
    ) {
      return false
    }

    // For date fields, compare using dayjs
    if (field === 'date') {
      if (!dayjs(value1.date).isSame(dayjs(value2.date), 'day')) {
        return false
      }
    } else {
      // For other fields, compare directly
      if (value1[field] !== value2[field]) {
        return false
      }
    }
  }

  return true
}

export const areNextDuesOutOfSync = (
  item1: MaintenanceItem & {
    maintenanceNextDue: MaintenanceNextDue[]
  },
  item2: MaintenanceItem & {
    maintenanceNextDue: MaintenanceNextDue[]
  }
) => {
  // If the tracked by component is different, then check if next due remaining time is different
  if (item1.trackedByComponentId !== item2.trackedByComponentId) {
    // Check if any of the next due status fields are different
    if (item1?.nextDueStatus !== item2?.nextDueStatus) {
      return true
    }

    // Check if remaining values are significantly different
    if (Math.abs(item1.remainingValue - item2.remainingValue) > 0.1) {
      return true
    }

    // Check if remaining days are different
    if (item1.remainingDays !== item2.remainingDays) {
      return true
    }

    // Check if calculated next due dates are different
    if (
      (item1.calculatedNextDueAt && !item2.calculatedNextDueAt) ||
      (!item1.calculatedNextDueAt && item2.calculatedNextDueAt) ||
      (item1.calculatedNextDueAt &&
        item2.calculatedNextDueAt &&
        dayjs(item1.calculatedNextDueAt)
          .startOf('day')
          .diff(dayjs(item2.calculatedNextDueAt).startOf('day'), 'day') !== 0)
    ) {
      return true
    }
    return false
  }
  if (
    !areMaintenanceNextDuesEffectivelyEqual(
      item1.maintenanceNextDue?.[0],
      item2.maintenanceNextDue?.[0],
      item1.cadenceValue as CadenceValue,
      item2.cadenceValue as CadenceValue
    )
  ) {
    return true
  }
  return false
}
